import { useTranslation } from 'react-i18next';
import FormModal from './FormModal';

/** Form definitions for all farm entities. Each returns a FormModal with the right fields. */

export function ContractForm({ initial, onSave, onCancel, onDelete }) {
  const { t } = useTranslation();
  const fields = [
    { key: 'contract_type', label: t('form.contractType'), type: 'select', required: true,
      options: ['LAND_LEASE','SUPPLIER','OFFTAKE','ENERGY_PPA','INSURANCE','LOAN','CONTRACTOR','OTHER'].map(v => ({ value: v, label: t(`contracts.${v}`) })) },
    { key: 'counterparty_name', label: t('form.counterparty'), type: 'text', required: true, placeholder: 'z.B. Müller Landverpachtung' },
    { key: 'description', label: t('form.description'), type: 'textarea' },
    { key: 'payment_amount', label: t('form.amount'), type: 'number', required: true },
    { key: 'payment_frequency', label: t('form.frequency'), type: 'select',
      options: [{ value: 'MONTHLY', label: t('form.monthly') }, { value: 'QUARTERLY', label: t('form.quarterly') }, { value: 'ANNUALLY', label: t('form.annually') }, { value: 'ONE_TIME', label: t('form.oneTime') }] },
    { key: 'start_date', label: t('form.startDate'), type: 'date' },
    { key: 'end_date', label: t('form.endDate'), type: 'date' },
    { key: 'notice_period_days', label: t('form.noticeDays'), type: 'number' },
    { key: 'auto_renew', label: t('form.autoRenew'), type: 'toggle' },
  ];
  return <FormModal title={initial ? t('form.editContract') : t('form.addContract')} fields={fields} initialValues={initial} onSave={onSave} onCancel={onCancel} onDelete={onDelete} />;
}

export function InventoryForm({ initial, onSave, onCancel, onDelete }) {
  const { t } = useTranslation();
  const fields = [
    { key: 'item_type', label: t('form.itemType'), type: 'select', required: true,
      options: ['GRAIN','FERTILIZER','PPP','SEED','FUEL','OTHER'].map(v => ({ value: v, label: t(`form.inv_${v}`) })) },
    { key: 'item_name', label: t('form.itemName'), type: 'text', required: true, placeholder: 'z.B. Winterweizen, Harnstoff' },
    { key: 'quantity', label: t('form.quantity'), type: 'number', required: true },
    { key: 'unit', label: t('form.unit'), type: 'select', options: ['t','kg','l','Stk'].map(v => ({ value: v, label: v })), defaultValue: 't' },
    { key: 'purchase_price_per_unit', label: t('form.pricePerUnit'), type: 'number' },
    { key: 'quality_notes', label: t('form.qualityNotes'), type: 'textarea', placeholder: 'z.B. 13% Feuchte' },
    { key: 'expiry_date', label: t('form.expiryDate'), type: 'date' },
  ];
  return <FormModal title={initial ? t('form.editInventory') : t('form.addInventory')} fields={fields} initialValues={initial} onSave={onSave} onCancel={onCancel} onDelete={onDelete} />;
}

export function InsuranceForm({ initial, onSave, onCancel, onDelete }) {
  const { t } = useTranslation();
  const fields = [
    { key: 'policy_type', label: t('form.policyType'), type: 'select', required: true,
      options: ['HAIL','MULTI_PERIL_CROP','LIVESTOCK','BUILDING','LIABILITY','MACHINERY_BREAKDOWN','LEGAL','OTHER'].map(v => ({ value: v, label: t(`form.ins_${v}`) })) },
    { key: 'insurer_name', label: t('form.insurer'), type: 'text', required: true, placeholder: 'z.B. Vereinigte Hagel' },
    { key: 'premium_annual', label: t('form.premiumAnnual'), type: 'number', required: true },
    { key: 'deductible', label: t('form.deductible'), type: 'number' },
    { key: 'insured_value', label: t('form.insuredValue'), type: 'number' },
    { key: 'coverage_description', label: t('form.coverage'), type: 'textarea' },
    { key: 'start_date', label: t('form.startDate'), type: 'date' },
    { key: 'end_date', label: t('form.endDate'), type: 'date' },
    { key: 'auto_renew', label: t('form.autoRenew'), type: 'toggle', defaultValue: true },
  ];
  return <FormModal title={initial ? t('form.editInsurance') : t('form.addInsurance')} fields={fields} initialValues={initial} onSave={onSave} onCancel={onCancel} onDelete={onDelete} />;
}

export function FieldForm({ initial, onSave, onCancel, onDelete }) {
  const { t } = useTranslation();
  const fields = [
    { key: 'name', label: t('form.fieldName'), type: 'text', required: true, placeholder: 'z.B. Großer Schlag' },
    { key: 'area_ha', label: t('form.areaHa'), type: 'number', required: true },
    { key: 'current_crop', label: t('form.currentCrop'), type: 'select',
      options: ['wheat','barley','rapeseed','corn','sugarbeet','potato','rye','oats','grassland','sunflower'].map(v => ({ value: v, label: t(`crops.${v}`) || v })) },
    { key: 'soil_type', label: t('form.soilType'), type: 'select',
      options: [{ value: 'Sand', label: 'Sand' }, { value: 'Lehm', label: 'Lehm' }, { value: 'Ton', label: 'Ton' }, { value: 'Schluff', label: 'Schluff' }, { value: 'Moor', label: 'Moor' }] },
    { key: 'soil_quality_index', label: t('form.soilQuality'), type: 'number', placeholder: '0-100 Bodenpunkte' },
    { key: 'latitude', label: t('form.latitude'), type: 'number' },
    { key: 'longitude', label: t('form.longitude'), type: 'number' },
  ];
  return <FormModal title={initial ? t('form.editField') : t('form.addField')} fields={fields} initialValues={initial} onSave={onSave} onCancel={onCancel} onDelete={onDelete} />;
}

export function MachineForm({ initial, onSave, onCancel, onDelete }) {
  const { t } = useTranslation();
  const fields = [
    { key: 'machine_type', label: t('form.machineType'), type: 'select',
      options: ['Traktor','Mähdrescher','Feldhäcksler','Pflug','Grubber','Drillmaschine','Spritze','Düngerstreuer','Ladewagen','Güllefass','Sonstige'].map(v => ({ value: v, label: v })) },
    { key: 'make', label: t('form.make'), type: 'text', placeholder: 'z.B. Fendt, Claas' },
    { key: 'model', label: t('form.model'), type: 'text', placeholder: 'z.B. 724 Vario' },
    { key: 'name', label: t('form.machineName'), type: 'text', required: true },
    { key: 'year_of_manufacture', label: t('form.year'), type: 'number' },
    { key: 'engine_hp', label: t('form.engineHp'), type: 'number' },
    { key: 'ownership_type', label: t('form.ownership'), type: 'select',
      options: [{ value: 'OWNED', label: t('form.owned') }, { value: 'LEASED', label: t('form.leased') }, { value: 'SHARED', label: t('form.shared') }] },
  ];
  return <FormModal title={initial ? t('form.editMachine') : t('form.addMachine')} fields={fields} initialValues={initial} onSave={onSave} onCancel={onCancel} onDelete={onDelete} />;
}

export function HerdForm({ initial, onSave, onCancel, onDelete }) {
  const { t } = useTranslation();
  const fields = [
    { key: 'species', label: t('form.species'), type: 'select', required: true,
      options: ['DAIRY_CATTLE','BEEF_CATTLE','PIGS_FATTENING','PIGS_BREEDING','LAYING_HENS','BROILERS','SHEEP','GOATS','HORSES','OTHER'].map(v => ({ value: v, label: t(`form.sp_${v}`) })) },
    { key: 'breed', label: t('form.breed'), type: 'text', placeholder: 'z.B. Holstein Friesian' },
    { key: 'head_count', label: t('form.headCount'), type: 'number', required: true },
    { key: 'housing_type', label: t('form.housingType'), type: 'select',
      options: ['FREE_STALL','TIED_STALL','OPEN_BARN','PASTURE','DEEP_LITTER','SLATTED_FLOOR','OTHER'].map(v => ({ value: v, label: t(`form.ht_${v}`) })) },
    { key: 'facility_name', label: t('form.facilityName'), type: 'text' },
    { key: 'production_target', label: t('form.prodTarget'), type: 'text', placeholder: 'z.B. 8500 kg Milch/Kuh/Jahr' },
    { key: 'organic_certified', label: t('form.organic'), type: 'toggle' },
  ];
  return <FormModal title={initial ? t('form.editHerd') : t('form.addHerd')} fields={fields} initialValues={initial} onSave={onSave} onCancel={onCancel} onDelete={onDelete} />;
}

export function EnergyForm({ initial, onSave, onCancel, onDelete }) {
  const { t } = useTranslation();
  const fields = [
    { key: 'installation_type', label: t('form.installationType'), type: 'select', required: true,
      options: ['PV_ROOFTOP','PV_GROUND','BIOGAS','WIND','AGRI_PV','OTHER'].map(v => ({ value: v, label: t(`form.en_${v}`) })) },
    { key: 'name', label: t('form.installationName'), type: 'text', placeholder: 'z.B. Dach-PV Scheune' },
    { key: 'capacity_kw', label: t('form.capacityKw'), type: 'number', required: true },
    { key: 'feed_in_tariff_ct_kwh', label: t('form.feedInTariff'), type: 'number', placeholder: 'ct/kWh' },
    { key: 'self_consumption_pct', label: t('form.selfConsumption'), type: 'number', placeholder: '0-100 %', defaultValue: 30 },
    { key: 'commissioning_date', label: t('form.commissioningDate'), type: 'date' },
  ];
  return <FormModal title={initial ? t('form.editEnergy') : t('form.addEnergy')} fields={fields} initialValues={initial} onSave={onSave} onCancel={onCancel} onDelete={onDelete} />;
}
